import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Trophy, CheckCircle, Clock, MapPin, Calendar, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Award, Zap, Loader2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Prize, RankingEntry, usePrizes } from '@/hooks/usePrizes';
import { YearlyPrizeCalendar } from './YearlyPrizeCalendar';
import { PrizeFormModal } from './PrizeFormModal';
import { toast } from 'sonner';
import { format, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';

interface AdminPrizeManagerProps {
  adminId: string;
}

const PRIZE_TYPE_CONFIG: Record<string, { icon: typeof Trophy; label: string; color: string }> = {
  ranking_winner: { icon: Trophy, label: 'Ganador Ranking', color: 'text-white' },
  volume_bonus: { icon: TrendingUp, label: 'Bono Volumen', color: 'text-green-400' },
  achievement_badge: { icon: Award, label: 'Por Logro', color: 'text-purple-400' },
  special_event: { icon: Gift, label: 'Evento Especial', color: 'text-pink-400' },
  milestone: { icon: Trophy, label: 'Meta Cumplida', color: 'text-blue-400' },
};

// Calculate volume bonus based on sales
const calculateVolumeBonus = (bronceSales: number, plataSales: number, oroSales: number) => {
  let bonus = 0;
  bonus += Math.floor(bronceSales / 10) * 12000;
  bonus += Math.floor(plataSales / 10) * 20000;
  bonus += Math.floor(oroSales / 10) * 40000;
  return bonus;
};

// Get ranking prize based on position
const getRankingPrize = (position: number) => {
  if (position === 1) return 40000;
  if (position === 2) return 30000;
  if (position === 3) return 20000;
  return 0;
};

export function AdminPrizeManager({ adminId }: AdminPrizeManagerProps) {
  const { 
    prizes, 
    currentRanking, 
    createOrUpdatePrize, 
    assignWinner,
    markAsDelivered,
    formatMonthYear,
    getCurrentMonthYear,
    getPreviousMonthYear,
    refetch,
    fetchRankingForMonth,
  } = usePrizes();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthYear());
  const [selectedMonthRanking, setSelectedMonthRanking] = useState<RankingEntry[]>([]);
  const [isLoadingRanking, setIsLoadingRanking] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingPrize, setEditingPrize] = useState<Prize | null>(null);
  const [isAssigningWinners, setIsAssigningWinners] = useState(false);

  // Load ranking for selected month
  useEffect(() => {
    const loadRankingForMonth = async () => {
      setIsLoadingRanking(true);
      try {
        // For current month, use live data
        if (selectedMonth === getCurrentMonthYear()) {
          setSelectedMonthRanking(currentRanking);
        } else {
          // For other months, fetch from RPC
          const ranking = await fetchRankingForMonth(selectedMonth);
          setSelectedMonthRanking(ranking);
        }
      } finally {
        setIsLoadingRanking(false);
      }
    };

    loadRankingForMonth();
  }, [selectedMonth, currentRanking, fetchRankingForMonth, getCurrentMonthYear]);

  // Check if previous month has winners assigned
  const previousMonth = format(subMonths(new Date(), 1), 'yyyy-MM');
  const previousMonthWinners = prizes.filter(p => 
    p.month_year === previousMonth && 
    p.winner_id && 
    !p.isDefault
  );
  const hasAllWinnersAssigned = previousMonthWinners.length >= 3;

  const handleAutoAssignWinners = async () => {
    setIsAssigningWinners(true);
    try {
      const { data, error } = await supabase.functions.invoke('assign-monthly-winners');
      
      if (error) throw error;
      
      if (data?.success) {
        if (data.winners?.length > 0) {
          toast.success(`✅ ${data.winners.length} ganadores asignados para ${formatMonthYear(data.month_year)}`);
          await refetch();
        } else if (data.message) {
          toast.info(data.message);
        }
      } else {
        throw new Error(data?.error || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error assigning winners:', error);
      toast.error('Error al asignar ganadores automáticamente');
    } finally {
      setIsAssigningWinners(false);
    }
  };

  const handleMonthChange = (monthYear: string) => {
    setSelectedMonth(monthYear);
    // Update year selector if necessary
    const year = parseInt(monthYear.split('-')[0]);
    if (year !== selectedYear) {
      setSelectedYear(year);
    }
  };

  const currentMonthPrize = prizes.find(p => p.month_year === getCurrentMonthYear());
  const previousMonthPrize = prizes.find(p => p.month_year === getPreviousMonthYear());
  const selectedMonthPrize = prizes.find(p => p.month_year === selectedMonth);

  // Group prizes by month
  const prizesByMonth = prizes.reduce((acc, prize) => {
    if (!acc[prize.month_year]) {
      acc[prize.month_year] = [];
    }
    acc[prize.month_year].push(prize);
    return acc;
  }, {} as Record<string, Prize[]>);

  const handleSavePrize = async (data: {
    prizeName: string;
    prizeDescription: string;
    prizeType: string;
    prizeCategory: string;
    prizeValue: number | null;
  }) => {
    try {
      const existingPrize = editingPrize || prizes.find(p => p.month_year === selectedMonth);
      
      if (existingPrize) {
        // Update existing
        const { error } = await supabase
          .from('monthly_prizes')
          .update({
            prize_name: data.prizeName,
            prize_description: data.prizeDescription,
            prize_type: data.prizeType,
            prize_category: data.prizeCategory,
            prize_value: data.prizeValue,
          })
          .eq('id', existingPrize.id);
        
        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('monthly_prizes')
          .insert({
            month_year: selectedMonth,
            prize_name: data.prizeName,
            prize_description: data.prizeDescription,
            prize_type: data.prizeType,
            prize_category: data.prizeCategory,
            prize_value: data.prizeValue,
            status: 'pending',
          });
        
        if (error) throw error;
      }

      // Log activity
      await supabase.from('admin_audit_logs').insert({
        admin_user_id: adminId,
        target_user_id: adminId,
        action_type: existingPrize ? 'prize_updated' : 'prize_created',
        details: {
          month_year: selectedMonth,
          prize_name: data.prizeName,
          prize_type: data.prizeType,
          prize_value: data.prizeValue,
        },
      });

      toast.success(existingPrize ? 'Premio actualizado' : 'Premio creado');
      setEditingPrize(null);
      await refetch();
      return { success: true };
    } catch (error) {
      console.error('Error saving prize:', error);
      toast.error('Error al guardar');
      return { success: false };
    }
  };

  const handleAssignWinner = async (prizeId: string) => {
    if (selectedMonthRanking.length === 0) {
      toast.error('No hay vendedores en el ranking');
      return;
    }

    const winner = selectedMonthRanking[0];
    const result = await assignWinner(prizeId, (winner as any).seller_id);
    
    if (result.success) {
      // Log activity
      await supabase.from('admin_audit_logs').insert({
        admin_user_id: adminId,
        target_user_id: (winner as any).seller_id,
        action_type: 'prize_assigned',
        details: {
          prize_id: prizeId,
          winner_name: winner.nombre,
        },
      });
      
      toast.success(`Premio asignado a ${winner.nombre}`);
    } else {
      toast.error('Error al asignar ganador');
    }
  };

  const handleMarkDelivered = async (prize: Prize) => {
    const result = await markAsDelivered(prize.id, adminId);
    
    if (result.success) {
      // Log activity
      await supabase.from('admin_audit_logs').insert({
        admin_user_id: adminId,
        target_user_id: prize.winner_id || adminId,
        action_type: 'prize_delivered',
        details: {
          prize_id: prize.id,
          prize_name: prize.prize_name,
          month_year: prize.month_year,
        },
      });
      
      toast.success('Premio marcado como entregado');
    } else {
      toast.error('Error al actualizar');
    }
  };

  const handleDeletePrize = async (prizeId: string) => {
    try {
      const { error } = await supabase
        .from('monthly_prizes')
        .delete()
        .eq('id', prizeId);
      
      if (error) throw error;
      
      toast.success('Premio eliminado');
      await refetch();
    } catch (error) {
      console.error('Error deleting prize:', error);
      toast.error('Error al eliminar');
    }
  };

  const openCreateModal = () => {
    setEditingPrize(null);
    setFormModalOpen(true);
  };

  const openEditModal = (prize: Prize) => {
    setEditingPrize(prize);
    setFormModalOpen(true);
  };

  const getStatusBadge = (prize: Prize) => {
    const currentMonth = getCurrentMonthYear();
    
    if (prize.month_year === currentMonth && prize.status === 'pending') {
      return <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30"><Clock className="h-3 w-3 mr-1" />En curso</Badge>;
    }
    
    switch (prize.status) {
      case 'pending':
        return <Badge className="bg-zinc-500/20 text-zinc-300 border-zinc-500/30"><Clock className="h-3 w-3 mr-1" />Pendiente</Badge>;
      case 'claimable':
        return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30"><Gift className="h-3 w-3 mr-1" />Por reclamar</Badge>;
      case 'claimed':
        return <Badge className="bg-green-500/20 text-green-300 border-green-500/30"><CheckCircle className="h-3 w-3 mr-1" />Entregado</Badge>;
    }
  };

  const getPrizeTypeIcon = (prize: Prize) => {
    const type = (prize as any).prize_type || 'ranking_winner';
    const config = PRIZE_TYPE_CONFIG[type] || PRIZE_TYPE_CONFIG.ranking_winner;
    const Icon = config.icon;
    return <Icon className={`h-4 w-4 ${config.color}`} />;
  };

  // Get sorted months from prizes (for display)
  const sortedMonths = Object.keys(prizesByMonth)
    .filter(month => month.startsWith(selectedYear.toString()))
    .sort((a, b) => b.localeCompare(a));

  // Check if viewing current month
  const isCurrentMonth = selectedMonth === getCurrentMonthYear();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <Gift className="h-6 w-6 text-amber-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Gestión de Premios</h2>
            <p className="text-sm text-zinc-400">Configura premios para todo el año</p>
          </div>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Premio
        </Button>
      </div>

      {/* Auto-assign info card */}
      {!hasAllWinnersAssigned && previousMonthWinners.length === 0 && (
        <Card className="bg-zinc-900 border border-emerald-500/40">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/30 rounded-lg">
                <Zap className="h-5 w-5 text-emerald-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-400">
                  Asignación Automática Configurada
                </p>
                <p className="text-xs text-zinc-300">
                  Los ganadores se asignan automáticamente el 1° de cada mes a las 6:00 AM.
                </p>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                🥇 ₡40k • 🥈 ₡30k • 🥉 ₡20k
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year Navigation + Calendar - FIRST */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-zinc-400" />
              Vista Anual
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedYear(y => y - 1)}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-white font-medium w-16 text-center">{selectedYear}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedYear(y => y + 1)}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <YearlyPrizeCalendar
            prizes={prizes}
            year={selectedYear}
            onSelectMonth={handleMonthChange}
            selectedMonth={selectedMonth}
          />
        </CardContent>
      </Card>

      {/* Selected Month Prizes Card - Uniform 3 medal UI */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <Trophy className="h-5 w-5 text-amber-400" />
              Premios del Ranking - {formatMonthYear(selectedMonth)}
              {isCurrentMonth && (
                <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-xs ml-2">
                  En curso
                </Badge>
              )}
            </CardTitle>
            <Button
              size="sm"
              onClick={openCreateModal}
              className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Personalizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Standard ranking prizes - 3 medal cards */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-gradient-to-br from-amber-500/20 to-amber-900/30 rounded-xl p-4 border border-amber-500/30 text-center">
              <span className="text-3xl mb-2 block">🥇</span>
              <p className="text-sm text-zinc-400">1° Lugar</p>
              <p className="text-xl font-bold text-amber-400">₡40,000</p>
              {selectedMonthRanking[0] && (
                <p className="text-xs text-zinc-300 mt-2 truncate">
                  {selectedMonthRanking[0].nombre}
                </p>
              )}
            </div>
            <div className="bg-gradient-to-br from-zinc-400/20 to-zinc-800/30 rounded-xl p-4 border border-zinc-500/30 text-center">
              <span className="text-3xl mb-2 block">🥈</span>
              <p className="text-sm text-zinc-400">2° Lugar</p>
              <p className="text-xl font-bold text-zinc-300">₡30,000</p>
              {selectedMonthRanking[1] && (
                <p className="text-xs text-zinc-300 mt-2 truncate">
                  {selectedMonthRanking[1].nombre}
                </p>
              )}
            </div>
            <div className="bg-gradient-to-br from-orange-500/20 to-orange-900/30 rounded-xl p-4 border border-orange-500/30 text-center">
              <span className="text-3xl mb-2 block">🥉</span>
              <p className="text-sm text-zinc-400">3° Lugar</p>
              <p className="text-xl font-bold text-orange-400">₡20,000</p>
              {selectedMonthRanking[2] && (
                <p className="text-xs text-zinc-300 mt-2 truncate">
                  {selectedMonthRanking[2].nombre}
                </p>
              )}
            </div>
          </div>

          {/* Leader summary */}
          {selectedMonthRanking.length > 0 && (
            <div className="bg-black/20 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-zinc-400">Líder {isCurrentMonth ? 'actual' : 'del mes'}:</span>
                <span className="font-semibold text-white">{selectedMonthRanking[0].nombre}</span>
                <span className="text-amber-400">({selectedMonthRanking[0].total_score} pts)</span>
                <span className="text-xs text-green-400 font-medium ml-auto">
                  Total a pagar: ₡{(getRankingPrize(1) + calculateVolumeBonus(
                    selectedMonthRanking[0].bronce_sales, 
                    selectedMonthRanking[0].plata_sales, 
                    selectedMonthRanking[0].oro_sales
                  )).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cycle Progress Table - Using selected month data */}
      {selectedMonthRanking.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-400">
              <TrendingUp className="h-5 w-5" />
              Progreso del Ciclo - {formatMonthYear(selectedMonth)}
              {isLoadingRanking && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1">
              {isCurrentMonth ? 'Datos en vivo del ciclo actual' : `Datos históricos de ${formatMonthYear(selectedMonth)}`}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-700 hover:bg-transparent">
                    <TableHead className="text-zinc-400 sticky left-0 bg-zinc-900/90 z-10">Vendedor</TableHead>
                    <TableHead className="text-zinc-400 text-center whitespace-nowrap">Ventas (B/P/O)</TableHead>
                    <TableHead className="text-zinc-400 text-center">Canjes</TableHead>
                    <TableHead className="text-zinc-400 text-center whitespace-nowrap">Pts ventas</TableHead>
                    <TableHead className="text-zinc-400 text-center whitespace-nowrap">Pts hitos</TableHead>
                    <TableHead className="text-zinc-400 text-center whitespace-nowrap">Pts ahorro</TableHead>
                    <TableHead className="text-zinc-400 text-center whitespace-nowrap">Pts canjes</TableHead>
                    <TableHead className="text-zinc-400 text-center font-bold">TOTAL</TableHead>
                    <TableHead className="text-zinc-400 text-center">Rank</TableHead>
                    <TableHead className="text-zinc-400 text-center">Premio</TableHead>
                    <TableHead className="text-zinc-400 text-center whitespace-nowrap">Bonos ₡</TableHead>
                    <TableHead className="text-zinc-400 text-center font-bold whitespace-nowrap bg-green-900/30">TOTAL ₡</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedMonthRanking.map((seller, index) => {
                    const position = index + 1;
                    const rankingPrize = getRankingPrize(position);
                    const volumeBonus = calculateVolumeBonus(seller.bronce_sales, seller.plata_sales, seller.oro_sales);
                    
                    return (
                      <TableRow key={seller.seller_id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell className="font-medium text-white sticky left-0 bg-zinc-900/90 z-10">
                          {seller.nombre}
                        </TableCell>
                        <TableCell className="text-center text-zinc-300 whitespace-nowrap">
                          <span className="text-orange-400">{seller.bronce_sales}</span>
                          {' / '}
                          <span className="text-zinc-300">{seller.plata_sales}</span>
                          {' / '}
                          <span className="text-amber-400">{seller.oro_sales}</span>
                        </TableCell>
                        <TableCell className="text-center text-zinc-300">{seller.total_canjes}</TableCell>
                        <TableCell className="text-center text-zinc-300">{seller.membership_sale_points}</TableCell>
                        <TableCell className="text-center text-zinc-300">{seller.cycle_milestone_points}</TableCell>
                        <TableCell className="text-center text-zinc-300">{seller.savings_points}</TableCell>
                        <TableCell className="text-center text-zinc-300">{seller.redemption_points}</TableCell>
                        <TableCell className="text-center font-bold text-white">{seller.total_score}</TableCell>
                        <TableCell className="text-center">
                          {position === 1 && <span className="text-lg">🥇</span>}
                          {position === 2 && <span className="text-lg">🥈</span>}
                          {position === 3 && <span className="text-lg">🥉</span>}
                          {position > 3 && <span className="text-zinc-400">{position}</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          {rankingPrize > 0 ? (
                            <span className="text-amber-400 font-medium">
                              ₡{rankingPrize.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {volumeBonus > 0 ? (
                            <span className="text-green-400 font-medium">
                              ₡{volumeBonus.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center bg-green-900/20">
                          {(rankingPrize + volumeBonus) > 0 ? (
                            <span className="text-green-400 font-bold">
                              ₡{(rankingPrize + volumeBonus).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {/* Summary footer */}
            <div className="mt-4 pt-3 border-t border-zinc-800 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-400" />
                <span className="text-zinc-400">Bonos por volumen:</span>
                <span className="text-zinc-300">10 Bronce = ₡12k • 10 Plata = ₡20k • 10 Oro = ₡40k</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state for months with no data */}
      {selectedMonthRanking.length === 0 && !isLoadingRanking && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-8 text-center">
            <Trophy className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No hay datos de ranking para {formatMonthYear(selectedMonth)}</p>
            <p className="text-xs text-zinc-500 mt-1">
              {selectedMonth > getCurrentMonthYear() 
                ? 'Este mes aún no ha comenzado' 
                : 'No se registraron ventas en este período'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Prize History by Month */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Gift className="h-5 w-5 text-zinc-400" />
            Historial {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sortedMonths.length === 0 ? (
              <p className="text-center text-zinc-500 py-4">No hay premios registrados en {selectedYear}</p>
            ) : (
              sortedMonths.map((monthYear) => (
                <motion.div
                  key={monthYear}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-black/30 rounded-lg border border-zinc-800 overflow-hidden"
                >
                  <div className="px-4 py-2 bg-zinc-800/50 border-b border-zinc-700">
                    <h4 className="text-sm font-medium text-white capitalize">
                      {formatMonthYear(monthYear)}
                    </h4>
                  </div>
                  <div className="p-3 space-y-2">
                    {prizesByMonth[monthYear].map((prize) => (
                      <div
                        key={prize.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {getPrizeTypeIcon(prize)}
                          <div>
                            <p className="text-sm font-medium text-white">{prize.prize_name}</p>
                            {prize.winner_name && (
                              <p className="text-xs text-zinc-500 flex items-center gap-1">
                                <Trophy className="h-3 w-3 text-amber-400" />
                                {prize.winner_name}
                                {prize.winner_ubicacion && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {prize.winner_ubicacion}
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        {getStatusBadge(prize)}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Form Modal */}
      <PrizeFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        monthYear={selectedMonth}
        existingPrize={editingPrize}
        onSave={handleSavePrize}
        formatMonthYear={formatMonthYear}
      />
    </div>
  );
}
